import React, { useState, useMemo } from 'react';

// ── Math helpers ─────────────────────────────────────────────────────────────

function calcEMI(principal, annualRate, tenureMonths) {
  if (principal <= 0 || tenureMonths <= 0) return 0;
  const r = annualRate / 12 / 100;
  if (r === 0) return principal / tenureMonths;
  return (principal * r * Math.pow(1 + r, tenureMonths)) / (Math.pow(1 + r, tenureMonths) - 1);
}

function computePrepayAnalysis(principal, annualRate, tenureMonths, extraAmount) {
  if (principal <= 0 || tenureMonths <= 0 || extraAmount <= 0) return null;
  const r = annualRate / 12 / 100;
  const emi = calcEMI(principal, annualRate, tenureMonths);

  const newPrincipal = Math.max(0, principal - extraAmount);
  const originalTotalInterest = Math.max(0, emi * tenureMonths - principal);

  let newTenure, interestSaved;
  if (newPrincipal === 0) {
    newTenure = 0;
    interestSaved = originalTotalInterest;
  } else if (r === 0) {
    newTenure = Math.ceil(newPrincipal / emi);
    interestSaved = 0;
  } else {
    const ratio = (r * newPrincipal) / emi;
    if (ratio >= 1) return null;
    newTenure = Math.ceil(-Math.log(1 - ratio) / Math.log(1 + r));
    const newTotalInterest = Math.max(0, emi * newTenure - newPrincipal);
    interestSaved = Math.max(0, originalTotalInterest - newTotalInterest);
  }

  return { emi, interestSaved, newTenure, originalTenure: tenureMonths, originalTotalInterest };
}

// Tax benefit LOST by prepaying:
// Old regime home loan: Sec 24b (up to Rs.2L/yr interest deduction) + 80C (up to Rs.1.5L/yr principal)
// New regime or non-home: no deductions
function computeTaxBenefitLost(principal, annualRate, tenureMonths, newTenure, loanType, taxRegime, marginalRate) {
  if (taxRegime === 'new' || loanType !== 'home') return 0;
  const savedMonths = tenureMonths - newTenure;
  if (savedMonths <= 0 || marginalRate <= 0) return 0;
  const savedYears = savedMonths / 12;

  const r = annualRate / 12 / 100;
  const emi = calcEMI(principal, annualRate, tenureMonths);

  // Outstanding at month = newTenure (start of eliminated period)
  let outstandingAtCutoff = 0;
  if (r === 0) {
    outstandingAtCutoff = Math.max(0, principal - emi * newTenure);
  } else {
    outstandingAtCutoff = Math.max(0,
      principal * Math.pow(1 + r, newTenure) - emi * (Math.pow(1 + r, newTenure) - 1) / r
    );
  }

  const interestInEliminatedPeriod = Math.max(0, emi * savedMonths - outstandingAtCutoff);
  const principalInEliminatedPeriod = Math.max(0, outstandingAtCutoff);

  const sec24bDeductible = Math.min(interestInEliminatedPeriod, 200000 * savedYears);
  const cap80CDeductible = Math.min(principalInEliminatedPeriod, 150000 * savedYears);

  return (sec24bDeductible + cap80CDeductible) * (marginalRate / 100);
}

function computeInvestReturn(amount, annualReturn, tenureMonths) {
  const r = annualReturn / 12 / 100;
  if (r === 0) return 0;
  return amount * (Math.pow(1 + r, tenureMonths) - 1);
}

function marginalRateOldRegime(annualIncome) {
  const taxable = Math.max(0, annualIncome - 50000);
  if (taxable > 1000000) return 30;
  if (taxable > 500000) return 20;
  if (taxable > 250000) return 5;
  return 0;
}

const fmtRs = n => `Rs.\u200b${Math.round(Math.abs(n)).toLocaleString('en-IN')}`;

const LOAN_TYPES = [
  { val: 'home', label: 'Home Loan' },
  { val: 'personal', label: 'Personal' },
  { val: 'car', label: 'Car Loan' },
  { val: 'education', label: 'Education' },
];

const DebtIntelligence = ({ user, navigateTo, goBack }) => {
  const [multiMode, setMultiMode] = useState(false);

  // ── Single-loan state ─────────────────────────────────────────────────────
  const [loanType, setLoanType] = useState('home');
  const [principal, setPrincipal] = useState('');
  const [rate, setRate] = useState('');
  const [tenure, setTenure] = useState('');
  const [extraAmount, setExtraAmount] = useState('');
  const [assumedReturn, setAssumedReturn] = useState('12');
  const [taxRegime, setTaxRegime] = useState('new');
  const [annualIncome, setAnnualIncome] = useState('');
  const [mode, setMode] = useState('input'); // 'input' | 'result'

  // ── Multi-loan state ──────────────────────────────────────────────────────
  const [loans, setLoans] = useState([
    { id: 1, loanType: 'home', principal: '', rate: '', label: 'Loan 1' },
  ]);
  const [multiRegime, setMultiRegime] = useState('new');
  const [multiIncome, setMultiIncome] = useState('');

  const marginalRate = useMemo(() => {
    if (taxRegime === 'new') return 0;
    return marginalRateOldRegime(Number(annualIncome) || 0);
  }, [taxRegime, annualIncome]);

  const result = useMemo(() => {
    if (mode !== 'result') return null;
    const p = Number(principal), r = Number(rate), t = Number(tenure);
    const extra = Number(extraAmount), ret = Number(assumedReturn);
    if (!p || !r || !t || !extra) return null;

    const prepay = computePrepayAnalysis(p, r, t, extra);
    if (!prepay) return null;

    const taxLost = computeTaxBenefitLost(p, r, t, prepay.newTenure, loanType, taxRegime, marginalRate);
    const investReturn = computeInvestReturn(extra, ret, t);
    const netPrepay = prepay.interestSaved - taxLost;
    const prepayBetter = netPrepay > investReturn;
    const margin = Math.abs(netPrepay - investReturn);

    return { prepay, taxLost, investReturn, netPrepay, prepayBetter, margin, marginalRate };
  }, [mode, principal, rate, tenure, extraAmount, assumedReturn, loanType, taxRegime, marginalRate]);

  const multiRanked = useMemo(() => {
    const mr = multiRegime === 'old' ? marginalRateOldRegime(Number(multiIncome) || 0) : 0;
    return loans
      .filter(l => l.principal && l.rate)
      .map(l => {
        const isHome = l.loanType === 'home';
        const effectiveCost = (isHome && multiRegime === 'old')
          ? Number(l.rate) * (1 - mr / 100)
          : Number(l.rate);
        return { ...l, effectiveCost, mr };
      })
      .sort((a, b) => b.effectiveCost - a.effectiveCost);
  }, [loans, multiRegime, multiIncome]);

  const previewEMI = useMemo(() => {
    const p = Number(principal), r = Number(rate), t = Number(tenure);
    if (!p || !r || !t) return null;
    return calcEMI(p, r, t);
  }, [principal, rate, tenure]);

  const previewTotalInterest = useMemo(() => {
    if (!previewEMI || !Number(tenure) || !Number(principal)) return null;
    return Math.max(0, previewEMI * Number(tenure) - Number(principal));
  }, [previewEMI, tenure, principal]);

  const inp = {
    width: '100%', padding: '10px 12px', borderRadius: '8px',
    border: '1px solid var(--border)', backgroundColor: 'var(--bg)', color: 'var(--text-primary)',
    fontSize: '14px', outline: 'none', transition: 'border-color 0.2s',
    boxSizing: 'border-box', fontFamily: 'inherit',
  };
  const lbl = { display: 'block', fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.06em' };
  const focusIn = e => { e.target.style.borderColor = 'var(--accent)'; };
  const blurIn  = e => { e.target.style.borderColor = 'var(--border)'; };

  const returnLabel = (pct) => {
    const n = Number(pct);
    if (n >= 15) return 'aggressive (equity in a strong cycle)';
    if (n >= 10) return 'reasonable (broad equity index, 5+ yr horizon)';
    return 'conservative (debt funds / FDs)';
  };

  return (
    <section style={{ minHeight: '100vh', backgroundColor: 'var(--bg-page)', paddingTop: '80px' }}>
      <div style={{ maxWidth: '720px', margin: '0 auto', padding: '40px 24px 80px' }}>

        {/* Back */}
        <button onClick={goBack} style={{ background: 'none', border: 'none', color: 'var(--text-tertiary)', fontSize: '13px', cursor: 'pointer', padding: '0 0 16px', fontFamily: 'inherit' }}>← Back</button>

        {/* Header */}
        <div style={{ marginBottom: '28px' }}>
          <p style={{ fontSize: '11px', fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.12em', margin: '0 0 8px' }}>Debt / EMI Intelligence</p>
          <h1 style={{ fontSize: '28px', fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 8px', letterSpacing: '-0.02em' }}>Prepay or invest?</h1>
          <p style={{ fontSize: '15px', color: 'var(--text-secondary)', margin: 0, lineHeight: 1.6 }}>
            I have extra money this month — does it work harder paying off debt or growing as investment?
          </p>
        </div>

        {/* Mode tabs */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '28px' }}>
          {[{ label: 'Single loan', val: false }, { label: 'Rank multiple loans', val: true }].map(opt => (
            <button
              key={String(opt.val)}
              onClick={() => { setMultiMode(opt.val); setMode('input'); }}
              style={{
                padding: '8px 16px', borderRadius: '20px', fontSize: '13px', fontWeight: 500,
                cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s',
                backgroundColor: multiMode === opt.val ? 'var(--accent)' : 'transparent',
                color: multiMode === opt.val ? '#fff' : 'var(--text-secondary)',
                border: `1px solid ${multiMode === opt.val ? 'var(--accent)' : 'var(--border)'}`,
              }}
            >{opt.label}</button>
          ))}
        </div>

        {/* ── SINGLE LOAN INPUT ──────────────────────────────────────────────── */}
        {!multiMode && mode === 'input' && (
          <>
            {/* Step 1: Loan details */}
            <div style={{ backgroundColor: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius-card)', padding: '28px', marginBottom: '14px', boxShadow: 'var(--shadow-card)' }}>
              <p style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 20px' }}>Step 1 — Loan details</p>

              <div style={{ marginBottom: '16px' }}>
                <label style={lbl}>Loan type</label>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {LOAN_TYPES.map(lt => (
                    <button
                      key={lt.val} onClick={() => setLoanType(lt.val)}
                      style={{
                        padding: '7px 14px', borderRadius: '7px', fontSize: '13px', fontWeight: 500,
                        cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s',
                        backgroundColor: loanType === lt.val ? 'var(--accent)' : 'transparent',
                        color: loanType === lt.val ? '#fff' : 'var(--text-secondary)',
                        border: `1px solid ${loanType === lt.val ? 'var(--accent)' : 'var(--border)'}`,
                      }}
                    >{lt.label}</button>
                  ))}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={lbl}>Outstanding principal (₹)</label>
                  <input type="number" value={principal} onChange={e => setPrincipal(e.target.value)} placeholder="e.g. 2500000" style={inp} onFocus={focusIn} onBlur={blurIn} />
                </div>
                <div>
                  <label style={lbl}>Interest rate (% p.a.)</label>
                  <input type="number" step="0.1" value={rate} onChange={e => setRate(e.target.value)} placeholder="e.g. 8.5" style={inp} onFocus={focusIn} onBlur={blurIn} />
                </div>
                <div>
                  <label style={lbl}>Remaining tenure (months)</label>
                  <input type="number" value={tenure} onChange={e => setTenure(e.target.value)} placeholder="e.g. 180" style={inp} onFocus={focusIn} onBlur={blurIn} />
                </div>
                <div>
                  <label style={lbl}>Current EMI — auto-calculated</label>
                  <input
                    disabled
                    style={{ ...inp, backgroundColor: 'var(--bg-secondary)', color: 'var(--text-tertiary)', cursor: 'not-allowed' }}
                    value={previewEMI ? `${fmtRs(previewEMI)} / month` : ''}
                    placeholder="fill fields above"
                  />
                </div>
              </div>

              {previewEMI != null && previewTotalInterest != null && (
                <div style={{ marginTop: '14px', padding: '12px 14px', backgroundColor: 'rgba(0,194,124,0.05)', border: '1px solid rgba(0,194,124,0.15)', borderRadius: '8px' }}>
                  <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: 0 }}>
                    EMI: <strong style={{ color: 'var(--text-primary)' }}>{fmtRs(previewEMI)}/mo</strong>
                    {'  ·  '}Total interest if you continue: <strong style={{ color: 'var(--text-primary)' }}>{fmtRs(previewTotalInterest)}</strong>
                  </p>
                </div>
              )}
            </div>

            {/* Step 2: Extra amount + assumptions */}
            {principal && rate && tenure && (
              <div style={{ backgroundColor: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius-card)', padding: '28px', marginBottom: '14px', boxShadow: 'var(--shadow-card)' }}>
                <p style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 20px' }}>Step 2 — Your situation</p>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <label style={lbl}>Extra amount to deploy (₹)</label>
                    <input type="number" value={extraAmount} onChange={e => setExtraAmount(e.target.value)} placeholder="e.g. 100000" style={inp} onFocus={focusIn} onBlur={blurIn} />
                  </div>
                  <div>
                    <label style={lbl}>Tax regime</label>
                    <div style={{ display: 'flex', gap: '8px', height: '42px' }}>
                      {['old', 'new'].map(r => (
                        <button key={r} onClick={() => setTaxRegime(r)} style={{
                          flex: 1, borderRadius: '7px', fontSize: '13px', fontWeight: 500,
                          cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s',
                          backgroundColor: taxRegime === r ? '#1e293b' : 'transparent',
                          color: taxRegime === r ? '#fff' : 'var(--text-secondary)',
                          border: `1px solid ${taxRegime === r ? '#1e293b' : 'var(--border)'}`,
                        }}>{r.charAt(0).toUpperCase() + r.slice(1)} regime</button>
                      ))}
                    </div>
                  </div>

                  {taxRegime === 'old' && loanType === 'home' && (
                    <div>
                      <label style={lbl}>Annual income — for marginal rate</label>
                      <input type="number" value={annualIncome} onChange={e => setAnnualIncome(e.target.value)} placeholder="e.g. 1200000" style={inp} onFocus={focusIn} onBlur={blurIn} />
                      {annualIncome && (
                        <p style={{ fontSize: '11px', color: 'var(--text-tertiary)', margin: '5px 0 0' }}>Marginal rate: {marginalRate}%</p>
                      )}
                    </div>
                  )}

                  <div>
                    <label style={lbl}>
                      Assumed investment return (% p.a.)
                      <span style={{ color: 'var(--text-tertiary)', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}> — your assumption</span>
                    </label>
                    <input type="number" step="0.5" value={assumedReturn} onChange={e => setAssumedReturn(e.target.value)} style={inp} onFocus={focusIn} onBlur={blurIn} />
                    {assumedReturn && (
                      <p style={{ fontSize: '11px', color: 'var(--text-tertiary)', margin: '5px 0 0' }}>{Number(assumedReturn)}% is {returnLabel(assumedReturn)}</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {principal && rate && tenure && extraAmount && (
              <button
                onClick={() => setMode('result')}
                style={{ width: '100%', backgroundColor: 'var(--accent)', color: '#fff', border: 'none', borderRadius: '10px', padding: '14px', fontSize: '15px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', transition: 'background-color 0.2s' }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--accent-hover)'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = 'var(--accent)'}
              >Analyse →</button>
            )}
          </>
        )}

        {/* ── SINGLE LOAN RESULT ─────────────────────────────────────────────── */}
        {!multiMode && mode === 'result' && result && (
          <>
            {/* Verdict banner */}
            <div style={{
              backgroundColor: 'var(--bg)',
              border: '1px solid var(--border)',
              borderLeft: `3px solid ${result.prepayBetter ? 'var(--accent)' : '#6D28D9'}`,
              borderRadius: 'var(--radius-card)', padding: '28px 32px', marginBottom: '16px',
              boxShadow: 'var(--shadow-sm)',
            }}>
              <p style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 12px' }}>Verdict</p>
              <p style={{ fontSize: '13px', fontWeight: 500, color: 'var(--text-secondary)', margin: '0 0 6px' }}>
                {result.prepayBetter ? 'Prepaying your loan' : 'Investing the money'} is better by
              </p>
              <p style={{ fontSize: '40px', fontWeight: 800, color: result.prepayBetter ? 'var(--accent)' : '#6D28D9', margin: '0 0 16px', lineHeight: 1, letterSpacing: '-0.03em', fontVariantNumeric: 'tabular-nums' }}>
                {fmtRs(result.margin)}
              </p>
              <p style={{ fontSize: '14px', color: 'var(--text-secondary)', margin: 0, lineHeight: 1.7 }}>
                Prepaying saves <strong>{fmtRs(result.prepay.interestSaved)}</strong> in interest
                {loanType === 'home' && taxRegime === 'old' && result.taxLost > 0 && (
                  <>, but forfeits <strong>{fmtRs(result.taxLost)}</strong> in tax benefits (Sec 24b interest + 80C principal)</>
                )}.{' '}
                Investing instead earns an estimated <strong>{fmtRs(result.investReturn)}</strong> over {tenure} months at {assumedReturn}% p.a. <em style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>(your assumption)</em>.
              </p>
            </div>

            {/* Breakdown */}
            <div style={{ backgroundColor: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius-card)', padding: '20px', marginBottom: '14px', boxShadow: 'var(--shadow-card)' }}>
              <p style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 16px' }}>The numbers</p>
              {[
                { label: 'Interest saved by prepaying', val: fmtRs(result.prepay.interestSaved), color: 'var(--accent)', sign: '+' },
                ...(loanType === 'home' && taxRegime === 'old' && result.taxLost > 0 ? [
                  { label: `Tax deductions forfeited (Sec 24b + 80C, at ${result.marginalRate}% marginal rate)`, val: fmtRs(result.taxLost), color: '#F59E0B', sign: '–' },
                ] : []),
                { label: 'Net benefit of prepaying', val: fmtRs(result.netPrepay), color: result.prepayBetter ? 'var(--accent)' : '#EF4444', bold: true },
                null, // divider
                { label: `Expected investment returns at ${assumedReturn}% p.a. over ${tenure} months`, val: fmtRs(result.investReturn), color: '#8B5CF6', sign: '+' },
              ].map((row, i) => row === null ? (
                <div key={i} style={{ height: '1px', backgroundColor: 'var(--border)', margin: '10px 0' }} />
              ) : (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', padding: '8px 0', borderBottom: i < 2 ? '1px solid rgba(0,0,0,0.04)' : 'none' }}>
                  <span style={{ fontSize: '13px', color: 'var(--text-secondary)', fontWeight: row.bold ? 600 : 400, lineHeight: 1.45 }}>{row.label}</span>
                  <span style={{ fontSize: '14px', fontWeight: row.bold ? 700 : 600, color: row.color, whiteSpace: 'nowrap' }}>
                    {row.sign ? `${row.sign} ` : ''}{row.val}
                  </span>
                </div>
              ))}
            </div>

            {/* Loan outcome */}
            <div style={{ backgroundColor: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius-card)', padding: '20px', marginBottom: '14px', boxShadow: 'var(--shadow-card)' }}>
              <p style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 14px' }}>Your loan after prepaying</p>
              {[
                { label: 'Months saved', val: `${result.prepay.originalTenure - result.prepay.newTenure} months` },
                { label: 'New remaining tenure', val: result.prepay.newTenure === 0 ? 'Loan fully closed 🎉' : `${result.prepay.newTenure} months (${(result.prepay.newTenure / 12).toFixed(1)} years)` },
                { label: 'EMI stays at', val: `${fmtRs(result.prepay.emi)} / month` },
              ].map(({ label, val }) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '9px 0', borderBottom: '1px solid rgba(0,0,0,0.04)' }}>
                  <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>{label}</span>
                  <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)' }}>{val}</span>
                </div>
              ))}
            </div>

            {/* Disclaimer */}
            <div style={{ padding: '14px 16px', backgroundColor: 'rgba(0,0,0,0.02)', border: '1px solid var(--border)', borderRadius: '8px', marginBottom: '20px' }}>
              <p style={{ fontSize: '12px', color: 'var(--text-tertiary)', margin: 0, lineHeight: 1.65 }}>
                The {assumedReturn}% return is your assumption — not a guarantee. Actual equity returns vary significantly over shorter horizons. This analysis explains the trade-offs and math; it is not financial advice. Consult a SEBI-registered advisor for product-specific decisions.
                {loanType === 'home' && taxRegime === 'old' && ' Tax benefit uses old regime Sec 24b (up to Rs.2L/yr interest) and 80C (up to Rs.1.5L/yr principal). Assumes no other 80C investments for simplicity — adjust if your 80C is already maxed.'}
              </p>
            </div>

            <button
              onClick={() => setMode('input')}
              style={{ background: 'none', border: '1px solid var(--border)', color: 'var(--text-secondary)', borderRadius: '8px', padding: '10px 20px', fontSize: '13px', fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit' }}
            >← Recalculate</button>
          </>
        )}

        {/* ── MULTI-LOAN RANKING ─────────────────────────────────────────────── */}
        {multiMode && (
          <>
            <p style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '20px', lineHeight: 1.65 }}>
              Add all your active loans. We rank them by effective post-tax interest cost — highest effective cost = pay first.
            </p>

            {/* Regime + income */}
            <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div>
                <label style={lbl}>Tax regime</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {['old', 'new'].map(r => (
                    <button key={r} onClick={() => setMultiRegime(r)} style={{
                      padding: '8px 14px', borderRadius: '7px', fontSize: '13px', fontWeight: 500,
                      cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s',
                      backgroundColor: multiRegime === r ? '#1e293b' : 'transparent',
                      color: multiRegime === r ? '#fff' : 'var(--text-secondary)',
                      border: `1px solid ${multiRegime === r ? '#1e293b' : 'var(--border)'}`,
                    }}>{r.charAt(0).toUpperCase() + r.slice(1)} regime</button>
                  ))}
                </div>
              </div>
              {multiRegime === 'old' && (
                <div style={{ flex: 1, minWidth: '200px' }}>
                  <label style={lbl}>Annual income (for marginal rate)</label>
                  <input type="number" value={multiIncome} onChange={e => setMultiIncome(e.target.value)} placeholder="e.g. 1200000" style={inp} onFocus={focusIn} onBlur={blurIn} />
                </div>
              )}
            </div>

            {/* Loan rows */}
            {loans.map((loan) => (
              <div key={loan.id} style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: '12px', padding: '20px', marginBottom: '10px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '14px' }}>
                  <input
                    value={loan.label}
                    onChange={e => setLoans(ls => ls.map(l => l.id === loan.id ? { ...l, label: e.target.value } : l))}
                    style={{ ...inp, maxWidth: '200px', fontSize: '13px', fontWeight: 600, padding: '6px 10px' }}
                    onFocus={focusIn} onBlur={blurIn}
                  />
                  {loans.length > 1 && (
                    <button onClick={() => setLoans(ls => ls.filter(l => l.id !== loan.id))} style={{ background: 'none', border: 'none', color: '#EF4444', fontSize: '13px', cursor: 'pointer', fontFamily: 'inherit' }}>Remove</button>
                  )}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
                  <div>
                    <label style={lbl}>Type</label>
                    <select value={loan.loanType} onChange={e => setLoans(ls => ls.map(l => l.id === loan.id ? { ...l, loanType: e.target.value } : l))} style={inp}>
                      {LOAN_TYPES.map(lt => <option key={lt.val} value={lt.val}>{lt.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={lbl}>Principal (₹)</label>
                    <input type="number" value={loan.principal} onChange={e => setLoans(ls => ls.map(l => l.id === loan.id ? { ...l, principal: e.target.value } : l))} placeholder="e.g. 500000" style={inp} onFocus={focusIn} onBlur={blurIn} />
                  </div>
                  <div>
                    <label style={lbl}>Rate (% p.a.)</label>
                    <input type="number" step="0.1" value={loan.rate} onChange={e => setLoans(ls => ls.map(l => l.id === loan.id ? { ...l, rate: e.target.value } : l))} placeholder="e.g. 12.5" style={inp} onFocus={focusIn} onBlur={blurIn} />
                  </div>
                </div>
              </div>
            ))}

            <button
              onClick={() => setLoans(ls => [...ls, { id: Date.now(), loanType: 'personal', principal: '', rate: '', label: `Loan ${ls.length + 1}` }])}
              style={{ background: 'none', border: '1px dashed var(--border)', color: 'var(--text-tertiary)', borderRadius: '10px', padding: '12px', fontSize: '13px', cursor: 'pointer', width: '100%', fontFamily: 'inherit', marginBottom: '20px', transition: 'border-color 0.2s' }}
              onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--text-secondary)'}
              onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
            >+ Add another loan</button>

            {/* Ranking */}
            {multiRanked.length > 0 && (
              <div style={{ backgroundColor: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius-card)', padding: '20px 24px', boxShadow: 'var(--shadow-card)' }}>
                <p style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 16px' }}>Kill order — highest effective cost first</p>
                {multiRanked.map((loan, i) => (
                  <div key={loan.id} style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '12px 0', borderBottom: i < multiRanked.length - 1 ? '1px solid var(--border)' : 'none' }}>
                    <div style={{
                      width: '32px', height: '32px', borderRadius: '50%', flexShrink: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      backgroundColor: i === 0 ? 'rgba(239,68,68,0.1)' : 'rgba(0,0,0,0.04)',
                      border: `2px solid ${i === 0 ? '#EF4444' : 'var(--border)'}`,
                    }}>
                      <span style={{ fontSize: '13px', fontWeight: 700, color: i === 0 ? '#EF4444' : 'var(--text-tertiary)' }}>{i + 1}</span>
                    </div>
                    <div style={{ flex: 1 }}>
                      <p style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 2px' }}>
                        {loan.label} — {LOAN_TYPES.find(l => l.val === loan.loanType)?.label}
                      </p>
                      <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: 0 }}>
                        {loan.rate}% nominal → <strong>{loan.effectiveCost.toFixed(2)}% effective post-tax</strong>
                        {loan.loanType === 'home' && multiRegime === 'old' && <span style={{ color: 'var(--text-tertiary)' }}> (home loan interest deduction applied)</span>}
                      </p>
                    </div>
                    {i === 0 && (
                      <span style={{ fontSize: '11px', fontWeight: 700, color: '#EF4444', backgroundColor: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: '20px', padding: '3px 10px', flexShrink: 0 }}>Pay first</span>
                    )}
                  </div>
                ))}
                <div style={{ marginTop: '16px', padding: '12px', backgroundColor: 'rgba(0,0,0,0.02)', borderRadius: '8px' }}>
                  <p style={{ fontSize: '12px', color: 'var(--text-tertiary)', margin: 0, lineHeight: 1.65 }}>
                    Effective cost = nominal rate adjusted for tax deductibility. Home loans in the old regime have lower effective cost because interest is deductible under Sec 24b. Rankings show the right order to prioritise prepayment — this is analysis, not a recommendation to buy any investment product.
                  </p>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default DebtIntelligence;
